import React, { useState, useEffect, useRef } from 'react';
import { resolveStorefrontImageUrl, LuxWebCache } from '../api/client';

const Image = ({
    src,
    alt = '',
    className = '',
    style = {},
    fallbackSrc = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7',
    lazy = true,
    objectFit = 'cover',
    aspectRatio,
    ...props
}) => {
    const finalSrc = src ? resolveStorefrontImageUrl(src) : null;
    const isAlreadyDecoded = finalSrc ? LuxWebCache.isImageDecoded(finalSrc) : false;
    const [isLoaded, setIsLoaded] = useState(isAlreadyDecoded);
    const [hasError, setHasError] = useState(false);
    const imgRef = useRef(null);

    // Reset load state when src changes (preserving 0ms instant hit if already decoded in GPU RAM)
    useEffect(() => {
        const nextResolved = src ? resolveStorefrontImageUrl(src) : null;
        const alreadyReady = nextResolved ? LuxWebCache.isImageDecoded(nextResolved) : false;
        setIsLoaded(alreadyReady);
        setHasError(false);
    }, [src]);

    // Handle cached images
    useEffect(() => {
        if (imgRef.current && imgRef.current.complete) {
            if (finalSrc) LuxWebCache.markImageDecoded(finalSrc);
            setIsLoaded(true);
        }
    }, [src, finalSrc]);

    const finalFallback = resolveStorefrontImageUrl(fallbackSrc);
    const actualSrc = hasError || !finalSrc ? finalFallback : finalSrc;

    // Dynamic styles
    const containerStyle = {
        position: 'relative',
        overflow: 'hidden',
        ...(aspectRatio ? { aspectRatio } : {}),
        ...style
    };

    const imgStyle = {
        width: '100%',
        height: '100%',
        objectFit,
        opacity: isLoaded ? 1 : 0,
        transition: isAlreadyDecoded ? 'none' : 'opacity 0.16s ease-out',
        position: 'absolute',
        top: 0,
        left: 0
    };

    const renderFallback = () => {
        const initial = (alt || 'L').charAt(0).toUpperCase();
        return (
            <div
                style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'linear-gradient(135deg, var(--gold, #d4af37), #e4ca95)',
                    color: '#0d0e12',
                    fontFamily: 'var(--font-heading, sans-serif)',
                    fontWeight: 'bold',
                    fontSize: '1.2em',
                    ...imgStyle,
                    opacity: 1 // Override opacity for fallback
                }}
                {...props}
            >
                {initial}
            </div>
        );
    };

    return (
        <div
            className={`lux-image-wrapper ${className}`}
            style={containerStyle}
        >
            {(!isLoaded && !hasError && !!finalSrc) && (
                <div
                    className="skeleton skeleton-img"
                    style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}
                />
            )}

            {!hasError && finalSrc ? (
                <img
                    ref={imgRef}
                    src={actualSrc}
                    alt={alt}
                    style={imgStyle}
                    loading={isAlreadyDecoded ? "eager" : (lazy ? "lazy" : "eager")}
                    decoding="async"
                    onLoad={() => {
                        if (finalSrc) LuxWebCache.markImageDecoded(finalSrc);
                        setIsLoaded(true);
                    }}
                    onError={() => {
                        setHasError(true);
                        setIsLoaded(true);
                    }}
                    {...props}
                />
            ) : null}

            {(hasError || !finalSrc) && renderFallback()}
        </div>
    );
};

export default Image;
